import { useEffect, useMemo, useState } from 'react';
import { BiCloud, BiSearch } from 'react-icons/bi';
import { FaLocationDot } from 'react-icons/fa6';

import { Input } from '@/components/Inputs';
import { Skeleton } from '@/components/Skeleton';
import { GeolocationStatus, useGeolocation } from '@/hooks/useGeolocation';
import { useRoles } from '@/hooks/useRoles';
import {
  type GeoPlace,
  WeatherStatus,
  searchPlaces,
  useWeatherForecast,
} from '@/hooks/useWeatherForecast';
import { UserRole } from '@/utils/roles';

import { BaseWidget } from '../BaseWidget';

import { formatDayLabel, formatHourLabel, weatherCodeToEmoji } from './weatherCode';
import s from './WeatherWidget.module.scss';

const WINDY_BASE = 'https://embed.windy.com/embed2.html';
const SKELETON_HOURS = 6;

interface ForecastBlockProps {
  lat: number | null;
  lon: number | null;
  placeName: string;
  geoError: boolean;
  onPickPlace: (p: GeoPlace) => void;
}

const ForecastBlock = ({ lat, lon, placeName, geoError, onPickPlace }: ForecastBlockProps) => {
  const weather = useWeatherForecast(lat, lon);
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<GeoPlace[]>([]);
  const [searchOpen, setSearchOpen] = useState(false);

  useEffect(() => {
    if (!query.trim()) return;
    const controller = new AbortController();
    const timeout = setTimeout(() => {
      searchPlaces(query, controller.signal)
        .then(setSuggestions)
        .catch(() => undefined);
    }, 250);
    return () => {
      clearTimeout(timeout);
      controller.abort();
    };
  }, [query]);

  const upcoming = useMemo(() => {
    if (weather.status !== WeatherStatus.SUCCESS) return [];
    return weather.data.hours.slice(0, 24);
  }, [weather]);

  const handlePick = (p: GeoPlace) => {
    onPickPlace(p);
    setQuery('');
    setSuggestions([]);
    setSearchOpen(false);
  };

  const dayLabel = upcoming[0] ? formatDayLabel(upcoming[0].time) : '';

  return (
    <section className={s.forecast}>
      {weather.status === WeatherStatus.LOADING && (
        <div className={s.forecastSkeleton} aria-busy="true">
          <Skeleton variant="text" width={200} height={24} />
          <div className={s.hoursScroll}>
            {Array.from({ length: SKELETON_HOURS }, (_, i) => (
              <Skeleton key={i} variant="block" width={88} height={156} className={s.hourBone} />
            ))}
          </div>
        </div>
      )}
      {weather.status === WeatherStatus.ERROR && (
        <div className={s.state}>Couldn’t load the forecast. Please try again.</div>
      )}
      {geoError && lat === null && (
        <div className={s.state}>Please allow location access or search for a place below.</div>
      )}

      {weather.status === WeatherStatus.SUCCESS && upcoming.length > 0 && (
        <>
          <div className={s.header}>
            <div className={s.place}>
              <FaLocationDot /> {placeName}
            </div>
            <span className={s.dot} aria-hidden>
              •
            </span>
            <h4 className={s.day}>{dayLabel}</h4>
          </div>

          <div className={s.hoursScroll}>
            {upcoming.map((h) => (
              <article key={h.time} className={s.hourCard}>
                <header className={s.hourTime}>{formatHourLabel(h.time)}</header>
                <div className={s.hourIcon} aria-hidden>
                  {weatherCodeToEmoji(h.weatherCode)}
                </div>
                <div
                  className={s.windArrow}
                  style={{ transform: `rotate(${h.windDirectionDeg}deg)` }}
                  aria-hidden
                />
                <div className={s.wind}>{h.windSpeedKmh}km/h</div>
                <div className={s.temp}>{h.temperatureC}°</div>
              </article>
            ))}
          </div>
        </>
      )}

      <div className={s.search}>
        <Input
          placeholder="Antibes"
          value={query}
          startIcon={<BiSearch size={20} />}
          onChange={(e) => {
            const v = e.target.value;
            setQuery(v);
            if (!v.trim()) setSuggestions([]);
          }}
          onFocus={() => setSearchOpen(true)}
          onBlur={() => setTimeout(() => setSearchOpen(false), 150)}
        />
        {searchOpen && suggestions.length > 0 && (
          <ul className={s.suggestions}>
            {suggestions.map((p) => (
              <li key={`${p.latitude}-${p.longitude}-${p.name}`}>
                <button type="button" onMouseDown={() => handlePick(p)}>
                  <span className={s.suggestionName}>{p.name}</span>
                  <span className={s.suggestionMeta}>
                    {[p.admin1, p.country].filter(Boolean).join(', ')}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
};

interface WindyMapBlockProps {
  lat: number | null;
  lon: number | null;
}

const WindyMapBlock = ({ lat, lon }: WindyMapBlockProps) => {
  const [loadedSrc, setLoadedSrc] = useState<string>();
  const params = new URLSearchParams('zoom=10');
  if (lat !== null && lon !== null) {
    params.append('lat', String(lat));
    params.append('lon', String(lon));
  }
  const src = `${WINDY_BASE}?${params}`;

  return (
    <section className={s.iframe}>
      <iframe src={src} onLoad={() => setLoadedSrc(src)} onError={() => setLoadedSrc(src)} />
      {loadedSrc !== src && <Skeleton variant="block" className={s.mapSkeleton} />}
    </section>
  );
};

export const WeatherWidget = () => {
  const { userRoles } = useRoles();
  const geo = useGeolocation();
  const [place, setPlace] = useState<GeoPlace | null>(null);

  const lat =
    place?.latitude ?? (geo.status === GeolocationStatus.SUCCESS ? geo.coords.latitude : null);
  const lon =
    place?.longitude ?? (geo.status === GeolocationStatus.SUCCESS ? geo.coords.longitude : null);
  const placeName = place?.name ?? 'My Location';
  const isGuest = userRoles?.includes(UserRole.GUEST) ?? false;

  return (
    <BaseWidget headerIcon={<BiCloud />} headerTitle="Sea Conditions" className={s.weatherWidget}>
      {isGuest ? (
        <ForecastBlock
          lat={lat}
          lon={lon}
          placeName={placeName}
          geoError={geo.status === GeolocationStatus.ERROR}
          onPickPlace={setPlace}
        />
      ) : (
        <WindyMapBlock lat={lat} lon={lon} />
      )}
    </BaseWidget>
  );
};
